import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  Alert,
  Anchor,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle } from '@tabler/icons-react';
import { emissionReportImportsApi } from '../../services/emissionReportImportsApi';
import { getApiErrorMessage, getApiViolations } from '../../utils/errorHandling';
import { mapViolations } from '../../utils/mapViolations';
import { previewFooterState } from './previewFooterState';
import type { EmissionReportData, EmissionReportImport, EmissionReportMeasurement } from '../../types/entities';
import { assignmentParts } from './emissionReportRow';
import { computePreviewStats } from '../../components/emissionReports/previewStats';
import { PreviewNotes } from '../../components/emissionReports/PreviewNotes';
import { logEntryLabel } from '../../components/emissionReports/logEntryLabel';
import {
  EmissionReportMatrix,
  MATRIX_LABEL_COLUMN_WIDTH_PX,
} from '../../components/emissionReports/EmissionReportMatrix';
import { MassFlowPreviewSection } from '../../components/emissionReports/MassFlowPreviewSection';
import {
  canRemoveMeasurement,
  excludedPositions,
  removeMeasurement,
  restoreMeasurement,
} from '../../components/emissionReports/matrixEdits';

/**
 * Phase 35 (D-07): Vorschau UND Protokoll sind DIESELBE Route -- der Unterschied entsteht
 * ausschliesslich aus `readOnly = status !== 'open'`. Es gibt keinen zweiten Pfad und keine
 * zweite Route dafuer.
 *
 * Layout nach Sketch 020: Kopf und Fusszeile sind feste Zeilen einer Flex-Spalte, dazwischen
 * GENAU EIN Scrollbereich (Hinweise, Faltung, die Matrix aus Plan 35-12). Das Innenabstand-
 * Padding sitzt am ERSTEN KIND des Scrollbereichs, nicht am Scrollbereich selbst -- sonst blitzt
 * beim Scrollen ein Streifen ueber dem klebenden Kopf durch ("Was am Bauen auffiel",
 * Sketch-020-README). Die Hoehe (`calc(100vh - 52px)`) zieht nur die Padding-Summe von
 * `AppShell.Main` ab (`padding="lg"` + `pt="xl"`, siehe `AppLayout.tsx`) -- diese Seite hat
 * keinen weiteren Kopf ueber sich. Ungeprueft im echten Browser, das ist ausdruecklich der
 * Checkpoint in Plan 35-14.
 *
 * Phase 40 (Plan 40-09, D-17): unter der Matrix folgt -- im selben Scrollbereich, dasselbe
 * `form`/`sourceData` -- der Abschnitt `MassFlowPreviewSection` ("Massenströme für die
 * Frachtenberechnung"). Es bleibt EIN Upload, EINE Vorschau, EIN "Übernehmen": Speichern und
 * Übernehmen schicken `form.getValues()` unveraendert weiter, der Abschnitt haengt an genau
 * diesem `resultData`, kein zweiter Request. Ein Feldfehler zu einem Massenstrom-Einzelwert
 * (propertyPath `massFlows.<key>.entries.<i>.value`) kommt ueber denselben `mapViolations`-Weg wie
 * bei den Messungen an die betroffene Zelle (siehe `handleAccept` unten).
 *
 * `resultData` wird ueber ein direkt an die Dokumentstruktur gebundenes `@mantine/form`
 * bearbeitet (Modus `controlled`, Plan 35-12) -- kein zweites `useState` daneben. Gespeichert wird
 * NUR auf Knopfdruck (Punkt 6 der Durchsprache vom 14.09.2026, hebt D-03 auf): kein entprelltes
 * Zwischenspeichern mehr, keine Rueckfrage beim Verlassen der Seite mit ungespeicherten
 * Aenderungen -- das ist so entschieden, wie in den Meldeformularen. Der Knopf "Speichern" ist im
 * Ruhezustand immer aktiv, unabhaengig von Aenderungen (Review CR-01, Nutzerentscheidung
 * 14.09.2026: `form.isDirty()` erwies sich als unzuverlaessig, ein Speichern ohne Aenderung ist
 * harmlos -- siehe `previewFooterState.ts`). "Uebernehmen" schickt den aktuellen Stand im Body mit
 * (`accept(id, resultData)`, Plan 35.2-03) -- Speichern und Sperren in einem Request. Das
 * Loeschen einer Messung markiert sie seit Punkt 4 (WR-15/WR-12, 35.1-04) nur noch
 * (`excluded: true`) -- Laenge und Reihenfolge des Arrays bleiben unveraendert, es wird nicht mehr
 * neu durchnummeriert.
 */

const EMPTY_RESULT_DATA: EmissionReportData = {
  schemaVersion: 0,
  readable: true,
  measurementRange: { start: null, end: null },
  measurements: [],
  log: [],
};

function headline(status: EmissionReportImport['status']): string {
  if (status === 'accepted') {
    return 'Protokoll';
  }
  if (status === 'unreadable') {
    return 'Nicht lesbar';
  }
  return 'Vorschau';
}

/** `2026-09-09T07:20:00+02:00` -> `09.09.2026, 07:20 Uhr` (Anzeige, nicht Sortierschluessel --
 *  hier ist `Date` richtig, der Wert traegt Zeitzoneninformation, anders als die reinen Datums-
 *  strings oben). */
function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** `YYYY-MM-DD` -> `DD.MM.YYYY`, rein ueber Zeichenketten -- dieselbe Falle wie in
 *  `EmissionReportsPage.tsx`: `new Date(iso)` ergibt Mitternacht UTC und zeigt in einer Zone mit
 *  negativem Offset den Vortag. */
function formatIsoDate(iso: string): string {
  const [year, month, day] = iso.slice(0, 10).split('-');
  return day && month && year ? `${day}.${month}.${year}` : iso;
}

/** Der Messzeitraum ohne "Messzeitraum "-Praefix -- der Kopf beschriftet ihn separat. */
function measurementRangeText(entry: EmissionReportImport): string | null {
  const { measurementRangeStart: start, measurementRangeEnd: end } = entry;
  if (!start && !end) {
    return null;
  }
  if (start && end && start !== end) {
    return `${formatIsoDate(start)} – ${formatIsoDate(end)}`;
  }
  return formatIsoDate((start ?? end) as string);
}

/** Datum/Betriebszustand/Zeitraum einer verworfenen Messung fuer den Rueckgaengig-Text. */
function measurementLabel(measurement: EmissionReportMeasurement): string {
  const date = measurement.date ? formatIsoDate(measurement.date) : measurement.dateRaw ?? '—';
  const rest = [measurement.operatingMode, measurement.time].filter(Boolean).join(', ');
  return rest ? `${date} (${rest})` : date;
}

/** Ein beschriftetes Feld der Zuordnungszeile ("Werk **Wilhelmshaven**", Sketch 020 `.vhead .l2`). */
function HeaderField({ label, value }: { label: string; value: string }) {
  return (
    <Text size="sm" c="dimmed">
      {label}{' '}
      <Text span fw={600} c="var(--mantine-color-vdzGray-9)">
        {value}
      </Text>
    </Text>
  );
}

export function EmissionReportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const importId = Number(id);

  const [entry, setEntry] = useState<EmissionReportImport | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [saving, setSaving] = useState(false);
  // Punkt 4 (WR-15/WR-12, 35.1-04): Stapel der Positionen ausgeschlossener Messungen, zuletzt
  // geloeschte hinten -- ersetzt den fruehren Einzelplatz `deleted`. Jeder Klick auf "Rueckgaengig"
  // holt die oberste Position zurueck. `loadEntry()` befuellt den Stapel aus `excludedPositions()`
  // beim Laden, damit ein Neuladen der Seite den Widerruf nicht mehr verliert.
  const [deletedStack, setDeletedStack] = useState<number[]>([]);

  const form = useForm<EmissionReportData>({
    mode: 'controlled',
    initialValues: EMPTY_RESULT_DATA,
  });

  const loadEntry = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const loaded = await emissionReportImportsApi.getOne(importId);
      setEntry(loaded);
      setLoadError(null);
      const values = loaded.resultData ?? EMPTY_RESULT_DATA;
      form.setValues(values);
      // Der Stapel wird aus dem geladenen Stand selbst rekonstruiert (nicht auf leer gesetzt) --
      // damit uebersteht "Rueckgaengig" ein Neuladen der Seite (Punkt 4).
      setDeletedStack(excludedPositions(values));
    } catch (err) {
      setLoadError(getApiErrorMessage(err, 'Dieser Importvorgang wurde nicht gefunden.'));
      setEntry(null);
    } finally {
      setLoading(false);
    }
    // form bewusst nicht als Dependency (stabile Referenz, siehe ReportEditPage.fetchReport).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importId]);

  useEffect(() => {
    void loadEntry();
  }, [loadEntry]);

  // D-13: Werk, Ofen und Meldung kommen als eingebettete Verweise mit ihrem HEUTIGEN Namen
  // mit (`emissionimport:read`). Die Seite laedt dafuer nichts nach -- steht der Verweis, gilt
  // sein Name; ist er weg, faellt `assignmentParts` auf den eingefrorenen Namen mit
  // "(gelöscht)" zurueck.
  const parts = useMemo(
    () => (entry === null ? { plant: null, kiln: null, period: null } : assignmentParts(entry)),
    [entry]
  );

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  if (entry === null) {
    return (
      <Stack gap="md">
        <Anchor component={Link} to="/emission-reports" size="sm">
          ‹ Emissionsberichte
        </Anchor>
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          {loadError ?? 'Dieser Importvorgang wurde nicht gefunden.'}
        </Alert>
      </Stack>
    );
  }

  // D-07: EIN Merkmal entscheidet ueber Bearbeitbarkeit -- kein zweiter Pfad, keine zweite
  // Route fuer das Protokoll.
  const readOnly = entry.status !== 'open';
  const range = measurementRangeText(entry);
  // Live aus dem Formular statt aus dem geladenen `entry.resultData` -- die Zahlen sollen jede
  // Bearbeitung sofort widerspiegeln (Loeschen einer Messung, Zellen leeren/fuellen).
  const liveResultData = form.getValues();
  const stats = computePreviewStats(liveResultData);
  // Punkt 6/5, Review CR-01: reine Ableitung der Fusszeilen-Knoepfe. "Speichern" ist im
  // Ruhezustand immer aktiv (siehe previewFooterState.ts), keine Dirty-Verfolgung mehr noetig.
  const footer = previewFooterState({
    saving,
    accepting,
    measurementCount: stats.measurementCount,
  });
  // Oberste Position des Rueckgaengig-Stapels, aufgeloest auf die lebende Messung (Punkt 4).
  const topDeletedMeasurement: EmissionReportMeasurement | null =
    deletedStack.length > 0
      ? (liveResultData.measurements[deletedStack[deletedStack.length - 1]] ?? null)
      : null;
  // Punkt 6, Fusszeile ohne Ueberlauf bei 1280 px (35.1-06, Beobachtung 1): der volle Text steht
  // im `title`, sichtbar wird er einzeilig mit Ellipse gekuerzt. `statsSummaryRest` haelt den
  // Text ohne die fett gesetzte Zahl -- kein Zerlegen des fertigen Strings.
  const statsSummaryRest = readOnly
    ? `Werte übernommen · ${stats.emptyCount} Felder leer · ${stats.measurementCount} Messungen angelegt`
    : `Werte werden übernommen · ${stats.emptyCount} Felder bleiben leer · ${stats.measurementCount} Messungen`;
  const statsSummaryText = `${stats.acceptedCount} ${statsSummaryRest}`;
  const undoLabel = topDeletedMeasurement
    ? `Messung ${topDeletedMeasurement.index} vom ${measurementLabel(topDeletedMeasurement)} wird nicht angelegt`
    : null;
  const environmentalDataId = entry.environmentalData?.id ?? null;
  // Fuer die Kollisionsmeldung: der lebende Meldungsjahrgang, solange die Meldung steht, sonst
  // der eingefrorene (D-13) -- fuer einen bereits uebernommenen Eintrag mit geloeschter Meldung
  // ist das die einzige Auskunft, die noch existiert.
  const reportYear =
    (entry.environmentalData?.reportingPeriod ?? entry.reportingPeriodAtImport)?.slice(0, 4) ??
    null;

  const entryId = entry.id;

  /**
   * Speichert den aktuellen Formularstand auf Knopfdruck (Punkt 6) -- kein automatisches
   * Zwischenspeichern mehr. Der Knopf "Speichern" bleibt danach aktiv (Review CR-01, kein
   * Dirty-Abgleich mehr) -- ein erneutes Speichern desselben Stands ist harmlos. Ein Fehler
   * erscheint als Notification, der Stand im Formular bleibt unveraendert -- der Nutzer kann
   * erneut versuchen.
   */
  async function handleSave(): Promise<void> {
    setSaving(true);
    try {
      const values = form.getValues();
      await emissionReportImportsApi.update(entryId, { resultData: values });
      notifications.show({
        title: 'Gespeichert',
        message: 'Die Änderungen an der Vorschau sind gespeichert.',
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Nicht gespeichert',
        message: getApiErrorMessage(err, 'Die Änderungen konnten nicht gespeichert werden.'),
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  }

  /**
   * Uebernehmen = Speichern + Sperren in einem Request (Punkt 6, Plan 35.2-03): der aktuelle
   * Formularstand geht im Body von `POST …/accept` mit, das Backend schreibt ihn unter derselben
   * Zeilensperre, mit der es die Messungen baut -- kein separates `update()` mehr davor.
   *
   * Ruft nach Erfolg `loadEntry()` -- die Seite steht dann bereits im Protokollmodus (D-07, keine
   * eigene Navigation). Scheitert die Uebernahme (409/422 -- bereits uebernommen, freigegebene
   * Meldung, Ofen fehlt an der Meldung, beschaedigte Werte), speichert das Backend NICHTS: die
   * Aenderungen bleiben im Browser stehen, ein Feldfehler kommt ueber `mapViolations` an die
   * betroffene Zelle, die zusammenfassende Notification zeigt die Servermeldung.
   */
  async function handleAccept(): Promise<void> {
    setAccepting(true);
    try {
      await emissionReportImportsApi.accept(entryId, form.getValues());
      await loadEntry();
      // Abnahmetest 21.09.2026: dass die Uebernahme gelaufen ist, war bisher allein am Wechsel
      // der Vorschau in den Lesezustand zu erkennen. Dieselbe Rueckmeldung wie beim Speichern.
      notifications.show({
        title: 'Übernommen',
        message: 'Die Messwerte stehen jetzt in der Meldung.',
        color: 'green',
      });
    } catch (err) {
      form.setErrors(mapViolations(getApiViolations(err)));
      notifications.show({
        title: 'Nicht übernommen',
        message: getApiErrorMessage(err, 'Der Bericht konnte nicht übernommen werden.'),
        color: 'red',
      });
    } finally {
      setAccepting(false);
    }
  }

  /**
   * Markiert eine Messung als ausgeschlossen, ohne Rueckfrage (widerrufbar bis "Uebernehmen") --
   * Sketch 020, Abweichung Punkt 4: die Messung bleibt im Dokument stehen (`excluded: true`)
   * statt entfernt zu werden, deshalb `position` statt eines Schnappschusses auf den Stapel.
   */
  function handleDeleteMeasurement(position: number): void {
    const current = form.getValues();
    if (!canRemoveMeasurement(current)) {
      notifications.show({
        message: 'Die letzte verbliebene Messung kann nicht gelöscht werden.',
        color: 'red',
      });
      return;
    }
    form.setValues(removeMeasurement(current, position));
    setDeletedStack((stack) => [...stack, position]);
  }

  /** Holt die zuletzt geloeschte Messung zurueck -- bei mehreren Loeschungen jeder Klick eine. */
  function handleUndoDelete(): void {
    if (deletedStack.length === 0) {
      return;
    }
    const position = deletedStack[deletedStack.length - 1];
    form.setValues(restoreMeasurement(form.getValues(), position));
    setDeletedStack((stack) => stack.slice(0, -1));
  }

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 52px)' }}>
      <Box
        pb="md"
        style={{ borderBottom: '1px solid var(--mantine-color-vdzGray-2)', flexShrink: 0 }}
      >
        <Anchor
          component={Link}
          to="/emission-reports"
          size="sm"
          mb="xs"
          style={{ display: 'inline-block' }}
        >
          ‹ Emissionsberichte
        </Anchor>
        <Group align="baseline" gap="sm" wrap="wrap">
          <Title order={1} size="h3">
            {headline(entry.status)}
          </Title>
          <Text c="dimmed" size="sm">
            {entry.originalFilename}
          </Text>
        </Group>
        <Group gap="lg" mt="xs" wrap="wrap">
          {parts.plant !== null && <HeaderField label="Werk" value={parts.plant} />}
          {parts.kiln !== null && <HeaderField label="Ofen" value={parts.kiln} />}
          {parts.period !== null && <HeaderField label="Meldung" value={parts.period} />}
          {range !== null && <HeaderField label="Messzeitraum" value={range} />}
        </Group>
      </Box>

      {/* GENAU EIN Scrollbereich -- Notizen und die Matrix (Punkt 13 vom 11.09.: ein zweiter
          Scroll-Container liesse den Tabellenkopf nicht mehr kleben). Waagerecht bewegt sich nur
          die Tabelle: der innere Block waechst mit ihr (`width: max-content`), der Scrollbereich
          selbst bleibt bei seiner sichtbaren Breite. Der Container (`container-type: inline-size`)
          sitzt EINE EBENE UNTER dem Scrollbereich, nicht an ihm selbst: an ihm gemessen zaehlt
          `100cqi` in Chromium die Breite der eigenen lotrechten Bildlaufleiste MIT (gemessen 1000 px
          gegen `clientWidth` 985 px bei 1280 px Fensterbreite), der Hinweisblock rutschte damit
          15 px unter die Leiste und verschob sich beim waagerechten Scrollen (Fund aus der
          Browserpruefung, Punkt 10). Am Kind direkt darunter ist `100cqi` die sichtbare Breite ohne
          die Leiste -- das war die Absicht der Durchsprache. Grenze: der waagerechte Scrollbalken
          bleibt am unteren Rand des Bereichs. Nebenwirkung: `container-type` macht den Bereich zum
          Bezugsrahmen fuer absolut und fixiert positionierte KINDER -- Mantine-Dropdowns rendern im
          Portal ausserhalb dieses Baums und sind davon nicht betroffen, im Browser bestaetigt
          (Task 3). Das Padding sitzt am ersten Kind, nicht hier am Scrollcontainer.
          `scrollPaddingLeft` haelt eine per Tastatur fokussierte Zelle (Punkt 9, Plan 06) sichtbar,
          statt sie unter der klebenden Merkmalsspalte der Matrix verschwinden zu lassen (Punkt 13). */}
      <Box
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          scrollPaddingLeft: MATRIX_LABEL_COLUMN_WIDTH_PX,
        }}
      >
        <Box style={{ containerType: 'inline-size' }}>
          <Box
            pt="md"
            style={
              entry.status === 'unreadable'
                ? undefined
                : { width: 'max-content', minWidth: '100%' }
            }
          >
            {entry.status === 'unreadable' ? (
              <Stack gap="sm">
                <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
                  <Text size="sm">
                    Dieser Bericht konnte nicht gelesen werden. Der Eintrag kann in der Liste der
                    Emissionsberichte gelöscht werden.
                  </Text>
                </Alert>
                <Stack gap={2}>
                  {(entry.resultData?.log ?? []).map((logEntry, index) => {
                    const label = logEntryLabel(logEntry);
                    return (
                      <Text key={index} size="sm" c="dimmed">
                        {label !== null && (
                          <Text span fw={600}>
                            {label}
                          </Text>
                        )}
                        {label !== null ? ` — ${logEntry.message}` : logEntry.message}
                      </Text>
                    );
                  })}
                </Stack>
              </Stack>
            ) : (
              <>
                {/* Klebt links in sichtbarer Breite (100cqi), waehrend die Matrix darunter frei
                    waagerecht scrollt (Punkt 10) -- die Hinweise selbst bekommen keine eigenen
                    Stile, nur diese Huelle. */}
                <Box style={{ position: 'sticky', left: 0, width: '100cqi' }}>
                  <PreviewNotes
                    kilnName={parts.kiln}
                    existingMeasurementCount={entry.existingMeasurementCount}
                    addedMeasurementCount={stats.measurementCount}
                    reportYear={reportYear}
                    environmentalDataId={environmentalDataId}
                    unreadEntries={stats.unreadEntries}
                    readOnly={readOnly}
                    existingMassFlowComponents={entry.existingMassFlowComponents}
                  />
                </Box>
                <Box mt="md">
                  <EmissionReportMatrix
                    form={form}
                    sourceData={entry.sourceData ?? EMPTY_RESULT_DATA}
                    readOnly={readOnly}
                    onDeleteMeasurement={handleDeleteMeasurement}
                  />
                </Box>
                <Box mt="md">
                  <MassFlowPreviewSection
                    form={form}
                    sourceData={entry.sourceData ?? EMPTY_RESULT_DATA}
                    readOnly={readOnly}
                  />
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Ist der Stand 'unreadable', gibt es nichts zu uebernehmen und nichts abzubrechen --
          die Fusszeile entfaellt ganz, die Seite endet mit Hinweis + Protokoll oben. */}
      {entry.status !== 'unreadable' && (
        <Box
          pt="md"
          style={{ borderTop: '1px solid var(--mantine-color-vdzGray-2)', flexShrink: 0 }}
        >
          {/* Punkt 6 (35.1-06, Beobachtung 1): feste Hoehe fuer BEIDE Zeilen links, unabhaengig
              davon, ob eine Messung ausgeschlossen ist -- vorher schob `wrap="nowrap"` zusammen
              mit `whiteSpace: nowrap` am Rueckgaengig-Hinweis "Abbrechen"/"übernehmen" bei
              1280 px aus dem Fenster. Der linke Block kuerzt jetzt selbst mit Ellipse
              (`title` traegt den vollen Text), die rechte Knopfgruppe bleibt `flexShrink: 0`. */}
          <Group justify="space-between" wrap="nowrap" gap="md" align="center">
            <Box style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <Box style={{ height: 22, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                <Text
                  size="sm"
                  c="dimmed"
                  title={statsSummaryText}
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  <Text span fw={600} c="var(--mantine-color-vdzGray-9)">
                    {stats.acceptedCount}
                  </Text>{' '}
                  {statsSummaryRest}
                </Text>
              </Box>
              {/* Rueckgaengig-Zeile -- feste Hoehe, damit Kopf und Fusszeile beim Loeschen einer
                  Messung nicht springen (Sketch 020, "Was am Bauen auffiel"). Bei mehreren
                  geloeschten Messungen zeigt sie die OBERSTE des Stapels; jeder Klick holt genau
                  diese zurueck (Punkt 4, WR-12). */}
              <Box style={{ height: 22, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                {!readOnly && undoLabel !== null && (
                  <Group gap={4} wrap="nowrap" style={{ minWidth: 0 }}>
                    <Text
                      size="sm"
                      c="dimmed"
                      title={undoLabel}
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {undoLabel}
                    </Text>
                    <Anchor
                      component="button"
                      type="button"
                      size="sm"
                      onClick={handleUndoDelete}
                      style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                    >
                      Rückgängig
                    </Anchor>
                  </Group>
                )}
              </Box>
            </Box>

            {readOnly ? (
              <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                Übernommen am {entry.acceptedAt ? formatDateTime(entry.acceptedAt) : '—'}
                {entry.acceptedByName ? ` von ${entry.acceptedByName}` : ''}
              </Text>
            ) : (
              <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
                <Button variant="default" onClick={() => navigate('/emission-reports')}>
                  Abbrechen
                </Button>
                <Button
                  variant="default"
                  onClick={() => void handleSave()}
                  loading={saving}
                  disabled={footer.saveDisabled}
                >
                  Speichern
                </Button>
                <Button
                  onClick={() => void handleAccept()}
                  loading={accepting}
                  disabled={footer.acceptDisabled}
                >
                  {stats.measurementCount === 1
                    ? '1 Messung übernehmen'
                    : `${stats.measurementCount} Messungen übernehmen`}
                </Button>
              </Group>
            )}
          </Group>
        </Box>
      )}
    </Box>
  );
}
